import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ComposedChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useTheme } from '../../contexts/ThemeContext'
import type { WeeklyTrendPoint } from '../../types/bruktbilfinansiering'

interface Props {
  data: WeeklyTrendPoint[]
  bankName: string
  loanDataCompleteThrough?: string  // ISO date string from API
}

type ChartMode = 'antall' | 'andel' | 'volum'

const _nb = new Intl.NumberFormat('nb-NO')

/** Format yyyy-mm-dd → dd.mm */
function fmtShort(d: string): string {
  const p = d.split('-')
  return p.length === 3 ? `${p[2]}.${p[1]}` : d
}

/** Derive week-end date (week_start + 6 days) as dd.mm */
function weekEndLabel(weekStart: string): string {
  const d = new Date(weekStart)
  d.setDate(d.getDate() + 6)
  return fmtShort(d.toISOString().slice(0, 10))
}

const modeLabelKey = {
  antall: 'shared.count',
  andel:  'shared.share',
  volum:  'shared.volume',
} as const satisfies Record<ChartMode, string>

const modeSubtitleKey = {
  antall: 'trend.subtitles.antall',
  andel:  'trend.subtitles.andel',
  volum:  'trend.subtitles.volum',
} as const satisfies Record<ChartMode, string>

const COLORS = {
  totalCount:    '#64748b', // slate  — totale salg
  bankCount:     '#0ea5e9', // cyan   — bankens lån
  bankShare:     '#10b981', // green  — markedsandel
  financedShare: '#64748b', // slate  — market rate line in Andel mode
}

// ── ChartDataPoint ────────────────────────────────────────────────────────────

interface ChartDataPoint extends WeeklyTrendPoint {
  label: string
  bank_share_pct: number
  financed_share_pct: number
}

// ── Tooltip ──────────────────────────────────────────────────────────────────

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{ payload: ChartDataPoint }>
  mode: ChartMode
  bankName: string
}

function CustomTooltip({ active, payload, mode, bankName }: CustomTooltipProps) {
  const { t } = useTranslation('bbf')
  if (!active || !payload?.length) return null
  const raw = payload[0].payload

  return (
    <div className="rounded-lg shadow-lg border p-3 text-xs bg-white dark:bg-dark-surface dark:border-dark-border min-w-[190px]">
      <p className="font-semibold mb-1.5 text-gray-900 dark:text-gray-100">
        {raw.label} ({fmtShort(raw.week_start)} – {weekEndLabel(raw.week_start)})
      </p>
      <div className="space-y-1">
        {mode === 'antall' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.totalCount }}>● {t('trend.totalSales')}</span>
              <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                {_nb.format(raw.total_count)}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.bankCount }}>● {t('trend.bankLoansOf', { bank: bankName })}</span>
              <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                {_nb.format(raw.bank_count)}
              </span>
            </div>
          </>
        )}
        {mode === 'andel' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.bankShare }}>● {t('overview.kpi.bankMarketShare')}</span>
              <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                {(raw.bank_share * 100).toFixed(1)} %
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.financedShare }}>● {t('trend.loanShareAllBanks')}</span>
              <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                {(raw.financed_share * 100).toFixed(1)} %
              </span>
            </div>
          </>
        )}
        {mode === 'volum' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.bankCount }}>● {t('trend.bankFinancingAtDealers')}</span>
              <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">
                {new Intl.NumberFormat('nb-NO', {
                  style: 'currency', currency: 'NOK', maximumFractionDigits: 0,
                }).format(raw.bank_total_nok)}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function UkentligTrendChart({ data, bankName, loanDataCompleteThrough }: Props) {
  const { t } = useTranslation('bbf')
  const { isDark } = useTheme()
  const [chartMode, setChartMode] = useState<ChartMode>('antall')

  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  // Cross-year label when period spans two calendar years
  const crossYear =
    data.length > 1 &&
    data[0].week_start.slice(0, 4) !== data[data.length - 1].week_start.slice(0, 4)

  // Build chart data — filter to loan_data_complete_through, then enrich with accumulated sums and percentage shares
  const chartData = useMemo(() => {
    const filteredData = loanDataCompleteThrough
      ? data.filter(pt => pt.week_start <= loanDataCompleteThrough)
      : data

    return filteredData.map(pt => ({
      ...pt,
      label: crossYear
        ? t('trend.weekCrossYear', { week: pt.week_number, yy: pt.week_start.slice(2, 4) })
        : t('trend.week', { week: pt.week_number }),
      bank_share_pct:     pt.bank_share * 100,
      financed_share_pct: pt.financed_share * 100,
    } as ChartDataPoint))
  }, [data, crossYear, loanDataCompleteThrough, t])

  // Average bank share — used as a dashed reference line in Andel mode
  const avgSharePct = useMemo(() => {
    if (!chartData.length) return null
    const sum = chartData.reduce((acc, pt) => acc + pt.bank_share, 0)
    return (sum / chartData.length) * 100
  }, [chartData])

  const xInterval = Math.max(0, Math.floor(chartData.length / 13) - 1)

  return (
    <div className="card p-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('trend.title')}
        </h2>

        {/* Mode toggle — pill button group matching Dashboard pattern */}
        <div className="flex rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
          {(['antall', 'andel', 'volum'] as ChartMode[]).map(mode => (
            <button
              key={mode}
              onClick={() => setChartMode(mode)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                chartMode === mode
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-dark-card text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover'
              }`}
            >
              {t(modeLabelKey[mode])}
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {t(modeSubtitleKey[chartMode])}
      </p>

      {/* ── Chart ── */}
      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 24, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />

          <XAxis
            dataKey="label"
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            interval={xInterval}
          />

          {/* Y-axis: Antall (absolute counts) */}
          {chartMode === 'antall' && (
            <YAxis
              yAxisId="count"
              orientation="left"
              domain={[0, 'auto']}
              allowDecimals={false}
              tick={{ fill: axisColor, fontSize: 11 }}
              tickLine={{ stroke: axisColor }}
              axisLine={{ stroke: axisColor }}
              tickFormatter={v => _nb.format(v)}
              width={56}
              label={{
                value: t('shared.count'),
                angle: -90,
                position: 'insideLeft',
                offset: 12,
                style: { fill: axisColor, fontSize: 11 },
              }}
            />
          )}

          {/* Y-axis: Andel (percentage) */}
          {chartMode === 'andel' && (
            <YAxis
              yAxisId="share"
              orientation="left"
              domain={[0, 'auto']}
              tickFormatter={v => `${v.toFixed(0)} %`}
              tick={{ fill: axisColor, fontSize: 11 }}
              tickLine={{ stroke: axisColor }}
              axisLine={{ stroke: axisColor }}
              width={56}
              label={{
                value: t('shared.share'),
                angle: -90,
                position: 'insideLeft',
                offset: 12,
                style: { fill: axisColor, fontSize: 11 },
              }}
            />
          )}

          {/* Y-axis: Volum (NOK amounts) */}
          {chartMode === 'volum' && (
            <YAxis
              yAxisId="nok"
              orientation="left"
              domain={[0, 'auto']}
              tick={{ fill: axisColor, fontSize: 11 }}
              tickLine={{ stroke: axisColor }}
              axisLine={{ stroke: axisColor }}
              tickFormatter={v =>
                new Intl.NumberFormat('nb-NO', {
                  notation: 'compact', maximumFractionDigits: 1,
                }).format(v) + ' kr'
              }
              width={64}
              label={{
                value: 'NOK',
                angle: -90,
                position: 'insideLeft',
                offset: 12,
                style: { fill: axisColor, fontSize: 11 },
              }}
            />
          )}

          <Tooltip content={<CustomTooltip mode={chartMode} bankName={bankName} />} />
          <Legend wrapperStyle={{ fontSize: 12, color: axisColor }} iconType="circle" />

          {/* ── Antall mode ── */}
          {chartMode === 'antall' && (
            <>
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="total_count"
                name={t('trend.totalSales')}
                stroke={COLORS.totalCount}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
              />
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="bank_count"
                name={t('shared.bankLoans')}
                stroke={COLORS.bankCount}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
              />
            </>
          )}

          {/* ── Andel mode ── */}
          {chartMode === 'andel' && (
            <>
              {avgSharePct !== null && (
                <ReferenceLine
                  yAxisId="share"
                  y={avgSharePct}
                  stroke={COLORS.bankShare}
                  strokeDasharray="4 3"
                  strokeOpacity={0.45}
                  label={{
                    value: t('trend.avg', { value: avgSharePct.toFixed(1) }),
                    position: 'insideTopRight',
                    style: { fill: axisColor, fontSize: 10 },
                  }}
                />
              )}
              <Line
                yAxisId="share"
                type="monotone"
                dataKey="bank_share_pct"
                name={t('overview.kpi.bankMarketShare')}
                stroke={COLORS.bankShare}
                strokeWidth={2.5}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
              />
              <Line
                yAxisId="share"
                type="monotone"
                dataKey="financed_share_pct"
                name={t('trend.loanShareAllBanks')}
                stroke={COLORS.financedShare}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </>
          )}

          {/* ── Volum mode ── */}
          {chartMode === 'volum' && (
            <Line
              yAxisId="nok"
              type="monotone"
              dataKey="bank_total_nok"
              name={t('trend.bankFinancingAtDealers')}
              stroke={COLORS.bankCount}
              strokeWidth={2}
              dot={{ r: 2 }}
              activeDot={{ r: 5 }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
